import { useEffect, useState } from 'react';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { Switch } from '@/components/ui/switch';
import { cookieCopyEN } from '@/lib/cookieCopy';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function CookieSettings({ open, onClose }: Props) {
  const { consent, saveManaged, setDraft, prefillForSettings } = useCookieConsent();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !initialized) {
      setLoading(true);
      // Fetch current preferences from backend (user or device level)
      prefillForSettings().then((prefs) => {
        // If backend returns preferences, use them; otherwise default to OFF
        const initial = prefs ?? { necessary: true, analytics: false, marketing: false, functional: false };
        setAnalytics(!!initial.analytics);
        setMarketing(!!initial.marketing);
        setFunctional(!!initial.functional);
        setDraft({ analytics: !!initial.analytics, marketing: !!initial.marketing, functional: !!initial.functional });
        setInitialized(true);
        setLoading(false);
      }).catch(() => {
        // On error, use current consent or default to OFF
        const initial = consent ?? { necessary: true, analytics: false, marketing: false, functional: false };
        setAnalytics(!!initial.analytics);
        setMarketing(!!initial.marketing);
        setFunctional(!!initial.functional);
        setDraft({ analytics: !!initial.analytics, marketing: !!initial.marketing, functional: !!initial.functional });
        setInitialized(true);
        setLoading(false);
      });
    } else if (!open && initialized) {
      // Reset initialized flag when modal closes
      setInitialized(false);
    }
  }, [open, consent, setDraft, initialized, prefillForSettings]);

  useEffect(() => {
    if (open && initialized && !loading) {
      setDraft({ analytics, marketing, functional });
    }
  }, [open, analytics, marketing, functional, setDraft, initialized, loading]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center overscroll-contain">
      <div className="absolute inset-0 bg-black/60 z-0" onClick={onClose} />
      <div
        className="relative z-10 w-full sm:max-w-2xl sm:mx-4 bg-white text-gray-900 shadow-2xl border border-gray-200 rounded-t-2xl sm:rounded-lg max-h-[90vh] sm:max-h-[85vh] h-[85vh] sm:h-auto flex flex-col animate-[settingsSlideUp_280ms_ease-out]"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-3 sm:px-6 sm:pt-6 sm:pb-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg sm:text-xl font-semibold">{cookieCopyEN.settings.title}</h3>
        </div>

        {/* Scrollable content */}
        <div className="px-5 sm:px-6 py-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : (
          <div className="space-y-6">
            {/* Necessary */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">{cookieCopyEN.settings.categories.necessary.title}</div>
                <div className="text-sm text-gray-600">{cookieCopyEN.settings.categories.necessary.description}</div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="inline-flex items-center gap-2 select-none cursor-not-allowed" aria-disabled="true" aria-label="Strictly necessary cookies cannot be disabled">
                    <div className="h-6 w-11 rounded-full bg-green-500 relative">
                      <div className="h-5 w-5 rounded-full bg-white shadow absolute left-[22px] top-0.5" />
                    </div>
                    On
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  Cannot be turned off for security and core functionality.
                </TooltipContent>
              </Tooltip>
              <div className="sr-only">On mobile: this setting is required and cannot be turned off.</div>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">{cookieCopyEN.settings.categories.analytics.title}</div>
                <div className="text-sm text-gray-600">{cookieCopyEN.settings.categories.analytics.description}</div>
              </div>
              <div className="inline-flex items-center gap-2">
                <Switch checked={analytics} onCheckedChange={(v: boolean) => setAnalytics(!!v)} />
                <span className={analytics ? 'text-green-600' : 'text-red-600'}>{analytics ? 'On' : 'Off'}</span>
              </div>
            </div>

            {/* Marketing */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">{cookieCopyEN.settings.categories.marketing.title}</div>
                <div className="text-sm text-gray-600">{cookieCopyEN.settings.categories.marketing.description}</div>
              </div>
              <div className="inline-flex items-center gap-2">
                <Switch checked={marketing} onCheckedChange={(v: boolean) => setMarketing(!!v)} />
                <span className={marketing ? 'text-green-600' : 'text-red-600'}>{marketing ? 'On' : 'Off'}</span>
              </div>
            </div>

            {/* Functional */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium">{'Functional'}</div>
                <div className="text-sm text-gray-600">{'Enhances site features and remembers preferences.'}</div>
              </div>
              <div className="inline-flex items-center gap-2">
                <Switch checked={functional} onCheckedChange={(v: boolean) => setFunctional(!!v)} />
                <span className={functional ? 'text-green-600' : 'text-red-600'}>{functional ? 'On' : 'Off'}</span>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Footer (sticky inside modal) */}
        <div className="px-5 sm:px-6 pb-[max(env(safe-area-inset-bottom),16px)] pt-3 border-t bg-white sticky bottom-0">
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
            <button onClick={onClose} className="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100">{cookieCopyEN.settings.buttons.cancel}</button>
            <button
              onClick={async () => {
                // Optimistic: close immediately, persist in background
                saveManaged({ analytics, marketing, functional });
                onClose();
              }}
              className="inline-flex justify-center rounded-md bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-600"
            >
              {cookieCopyEN.settings.buttons.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Inject slide-in keyframes once for the settings modal
;(function addCookieSettingsKeyframesOnce(){
  if (typeof document === 'undefined') return;
  const id = 'cookie-settings-anim';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.innerHTML = `@keyframes settingsSlideUp{from{transform:translateY(22px);opacity:.0}to{transform:translateY(0);opacity:1}}`;
  document.head.appendChild(style);
})();
